import React, { Component } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { Field } from 'redux-form';
import { Element } from 'react-scroll';
import cx from 'classnames';
import PublishFormLocationInput from '../LocationInput/PublishFormLocationInput/PublishFormLocationInput';
import OMCheckbox from '../OMComponents/OMCheckbox/OMCheckbox';
import { language, issueTypes, reportTypes, mapDefaultOptions, publishFormErrorNames } from '../../constants';
import GoogleService from '../../helpers/googleService';
// Helpers
import getLocalizedData from '../../utils/getLocalizedData';
import slugify from '../../helpers/slugify';
import styles from '../../containers/PublishForm/PublishPropertyForm.scss';
import { GeolocationService } from '../../services';
import MapboxService from '../../services/MapboxService/MapboxService';
import {
  addMultitouchGestures,
  removeMultitouchGestures
} from '../../services/MapboxService/helpers';

export default class PublishAddressBlock extends Component {
  state = {
    showTooltip: false,
    manualAddress: false,
    showManualButton: false,
    showConfirmationButton: false,
    showPreloader: false,
    city: '',
    street: '',
    country: ''
  };

  componentDidMount() {
    this._isMounted = true;
    this.initialiseGoogleService(this.props.locale);
    this._mapInitTimeout = setTimeout(() => {
      this.initMap();
    });
  }

  componentWillReceiveProps(newProps) {
    const localeChanged = (newProps.locale && newProps.locale !== this.props.locale);
    if (localeChanged) {
      MapboxService.changeMapLocale(this.map, newProps.locale);
      delete global.google;
      this.initialiseGoogleService(newProps.locale);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    clearTimeout(this._mapInitTimeout);
    MapboxService.cleanup();
    this.draggablePin && this.draggablePin.remove();
    if (this.props.isMobile && this.map) {
      removeMultitouchGestures(this.map);
    }
    this.map && this.map.remove();
    this.draggablePin = null;
    this.map = null;
  }

  onHideButton = () => {
    this.setState({ showConfirmationButton: false });
    this.isPinMoved = false;
    this.props.scrollToAddress();
  };

  getComponentInstance = (locationInputComponent) => {
    this.locationInputComponent = locationInputComponent;
  };

  getLocaleForGeocode = () => (this.props.locale === language.default) ? this.props.locale : language.fallback;

  setHomeNumberManually = (house_number) => {
    this.props.changeValue(this.props.name, {
      ...this.props.value,
      en: {
        ...this.props.value.en,
        house_number
      },
      he: {
        ...this.props.value.he,
        house_number
      }
    });
  };

  setPreloaderState = showPreloader => this.setState({ showPreloader });
  setManualButtonState = showManualButton => this.setState({ showManualButton });

  handleDragstart = () => {
    if (this.props.isMobile) {
      this._scrollPosition = window.pageYOffset;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this._scrollPosition}px`;
    }
  }

  handleDragend = () => {
    if (this.props.isMobile) {
      document.body.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo(0, this._scrollPosition);
    }

    const { lat, lng } = this.draggablePin.getLngLat();
    this.geocodeLocation(lat, lng);
  };

  handleMapLoad = () => {
    MapboxService.changeMapLocale(this.map, this.props.locale);
  }

  initMap = () => {
    const loc = get(this.props, 'value.location', { lat: '32.0836', lon: '34.781', isDefault: true });
    const isFull = get(this.props, 'value.isFull');
    const mapType = 'foreignPublish';
    const mapOptions = {
      ...MapboxService.getMapOptions(mapType)
    };

    if (loc && loc.lat) {
      mapOptions.center = {
        lat: loc.lat,
        lng: loc.lon
      };

      if (!loc.isDefault && !this.props.value?.zoom) {
        mapOptions.zoom = 17;
      }
    }

    MapboxService.createMapInstance(this.mapWrapper, mapOptions, this.props.locale).then((map) => {
      if (!map) { return; }

      if (!this._isMounted) {
        map.remove();
        return;
      }

      this.map = map;
      const { center: { lat, lng } } = mapOptions;
      this.draggablePin = MapboxService.createPin(lat, lng, {}, {
        draggable: true
      });

      this.draggablePin.addTo(this.map);

      this.draggablePin.on('dragstart', this.handleDragstart);
      this.draggablePin.on('dragend', this.handleDragend);

      if (this.props.isMobile) {
        addMultitouchGestures(this.map);
      }
    }).catch(error => console.warn('Failed to initialize publish address map.', error));
  };

  initialiseGoogleService = (locale) => {
    new GoogleService(locale, (gs) => { // eslint-disable-line no-new
      this.googleService = gs;
    });
  };

  geocodeAddress = (location) => {
    const locale = this.getLocaleForGeocode();
    const localizedAddress = location[locale] || {};

    const streetName = localizedAddress.street_name ? `${localizedAddress.street_name.replace(/ st.+$/gi, '')} ` : '';
    const houseNumber = localizedAddress.house_number ? `${localizedAddress.house_number}, ` : '';
    const cityName = localizedAddress.city_name ? `${localizedAddress.city_name}, ` : '';
    const countryName = localizedAddress.country_name ? localizedAddress.country_name : '';
    const neighborhoodName = (
      localizedAddress.neighborhood
      && localizedAddress.neighborhood !== localizedAddress.city_name
    ) ? localizedAddress.neighborhood : '';
    const address = `${streetName}${houseNumber}${cityName}${countryName}${neighborhoodName}`;

    const fallbackTags = [];
    if (cityName) {
      fallbackTags.push(cityName);
      if (houseNumber) {
        fallbackTags.push(streetName);
        fallbackTags.push(houseNumber);
      } else if (streetName) {
        fallbackTags.push(streetName);
      } else if (neighborhoodName) {
        fallbackTags.push(neighborhoodName);
      }
    }

    const cleanedTags = fallbackTags
      .filter(part => !!part)
      .map(keyPart => slugify(keyPart));

    const tags = location.tags && location.tags.length ? location.tags : cleanedTags;

    const geocode = this.googleService.geocodeLocusViaInternalBase(
      address, this.props.isForeign ? null : tags, localizedAddress.city_name, localizedAddress.country_name
    );

    geocode
      .then((response) => {
        this.handleLocationInput(response);
        const hideHouseNumber = get(this.props, 'value.hide_house_number');
        this.props.changeValue(this.props.name, { ...response, hide_house_number: hideHouseNumber });
        this.setState({ showPreloader: false });
      });
  };

  isIncludeUnnamed = str => str.toLocaleLowerCase().includes('unnamed');

  checkIsUnnamedRoad = (resp) => {
    const heStreet = get(resp, 'he.street_name', '') || '';
    const enStreet = get(resp, 'en.street_name', '') || '';
    const heFormatted = get(resp, 'he.formatted', '') || '';
    const enFormatted = get(resp, 'en.formatted', '') || '';

    return [heStreet, enStreet, heFormatted, enFormatted].some(this.isIncludeUnnamed);
  };

  geocodeLocation = (lat, lng) => {
    if (!this.state.manualAddress) {
      this.isPinMoved = true;
      this.googleService.geocodeByCoordinate({ lat, lng }, this.props.isForeign)
        .then((response) => {
          if (response && get(response, 'en.city_name') && !this.checkIsUnnamedRoad(response)) {
            response.location = { lat, lon: lng };
            this.handleLocationInput(response);
            const hideHouseNumber = get(this.props, 'value.hide_house_number');
            this.props.changeValue(this.props.name, { ...response, hide_house_number: hideHouseNumber });

            this.setState({ showConfirmationButton: true });
          } else {
            this.setState({ showConfirmationButton: false });
            this.props.changeValue(this.props.name, null);
          }

          this.setState({ showPreloader: false });
        });
    } else {
      this.props.changeValue(this.props.name, {
        ...this.props.value,
        location: { lat, lon: lng }
      });
    }
  };

  geocodeMyLocation = ({ lat, lng }) => this.geocodeLocation(lat, lng);

  clearAddressPart = (targetPart, query, clearHouse) => {
    const locale = this.getLocaleForGeocode();
    const location = {
      [locale]: {
        ...get(this.props, `value.${locale}`),
        house_number: ''
      },
      tags: get(this.props, 'value.tags')
    };

    if (targetPart === 'country_name') {
      this.setState({ country: query, city: '', street: '' });
      return this.handleLocationInput(null, false);
    }

    if (targetPart === 'city_name') {
      location[locale].street_name = '';
      location[locale].city_name = '';
      location[locale].neighborhood = '';
      this.setState({ city: query, street: '' });
      if (!this.props.isForeign) {
        return this.handleLocationInput(null, false);
      }
    }

    if (targetPart === 'street_name') {
      location[locale].street_name = '';
      this.setState({ street: query, city: '' });
      return this.googleService.autocompleteLocus(location[locale].city_name, null, 'city');
    }

    if (targetPart === 'house_number' && !clearHouse) {
      const data = { ...location, isFull: false };
      const updatedData = (this.props.value) ? { ...this.props.value, ...data } : data;

      return this.props.changeValue(this.props.name, updatedData);
    }

    return this.geocodeAddress(location);
  };

  clearMap = () => {
    const { lat, lng: lon } = this.props.isForeign ?
      MapboxService.getMapOptions('foreignPublish').center : mapDefaultOptions.center;
    MapboxService.changeMapZoom(this.map, { lat, lon });
  };

  saveAddressValue = (evt) => {
    evt.preventDefault();

    if (get(this.props, 'value.en.house_number')) {
      this.setState({
        manualAddress: false,
        showManualButton: false
      });
    }
    this.handleSelectLocation({
      ...this.props.value,
      isFull: true
    });
  }

  createAddress = (evt) => {
    evt.preventDefault();

    if (get(this.props, 'value.en.house_number')) {
      this.setState({
        manualAddress: false,
        showManualButton: false,
        showPreloader: true
      });

      const { value, token } = this.props;
      this.googleService.createAddressInLocus(value, token)
        .then(this.geocodeAddress)
        .catch((err) => {
          this.googleService.handleError(err);
          this.setState({ showPreloader: false });
          this.locationInputComponent.setError(publishFormErrorNames.addressWasNotCreated);
        });
    }
  };

  handleCancelManual = (evt) => {
    evt.preventDefault();
    this.setHomeNumberManually(null);
    this.setState({ manualAddress: false });
  };

  enableManualMode = (evt) => {
    evt.preventDefault();
    this.locationInputComponent.clearErrors(error => !error.startsWith('HOUSE') && !error.startsWith('ADDRESS'));
    this.setState({ manualAddress: true, showConfirmationButton: false });
    this.isPinMoved = false;
  };

  handleSelectLocation = ({ map: bounds, location, ...data }) => {
    this.props.changeValue(this.props.name, { location, ...data });

    if (!data.isFull && bounds && bounds.topright) {
      MapboxService.changeMapBoundsByViewport(this.map, bounds);
    }

    if (!data.isFull && bounds && bounds.west) {
      const boundsMap = [bounds.west, bounds.south, bounds.east, bounds.north];
      MapboxService.changeMapBounds(this.map, boundsMap);
    }

    if (data.isFull) {
      MapboxService.changeMapZoom(this.map, location, 17);
    }

    if (data.zoom) {
      const zoom = data.tags && data.tags.length === 3 ? 17 : data.zoom;
      MapboxService.changeMapZoom(this.map, location, zoom);
    }

    if (get(data, 'en.country_name')) {
      this.draggablePin?.setLngLat({ lat: location.lat, lng: location.lon });
    }

    if (get(data, 'en.city_name') && !this.checkIsUnnamedRoad(data)) {
      this.draggablePin?.setLngLat({ lat: location.lat, lng: location.lon });
    }
    this.setState({ showConfirmationButton: data.isFull });
  };

  handleLocationInput = (data, resetMap = true) => {
    if (!data) {
      this.props.changeValue(this.props.name, data);
      if (resetMap) {
        this.clearMap();
      }
    } else {
      const updatedData = (this.props.value)
        ? { ...this.props.value, ...data, tags: data.tags, place_id: data.place_id }
        : data;
      this.handleSelectLocation(updatedData);
      if (updatedData.isFull) {
        // !this.isPinMoved && this.props.scrollToAddress();
        this.props.changeValue(this.props.name, updatedData);
      }
    }
  };

  handleClickMyLocation = (evt) => {
    evt.preventDefault();
    this.setState({ showPreloader: true });
    GeolocationService.geolocate({ enableHighAccuracy: true })
      .then(this.geocodeMyLocation)
      .catch(error => console.error(error));
  };

  constructAddress = (addressData, locale) => {
    const lang = (addressData[locale]) ? locale : language.fallback;
    const address = `${addressData[lang].street_name} ${addressData[lang].house_number},
      ${addressData[lang].city_name}`;
    const addressString = this.props.isForeign ? `${address}, ${addressData[lang].country_name}` : address;
    return (addressData[lang].formatted)
      ? addressData[lang].formatted
      : addressString;
  };

  renderHideNumber = ({ input }) => {
    const inputLabel = (input.value)
      ? this.props.translate('showHouseNumber')
      : this.props.translate('hideHouseNumber');

    return (<div className={styles.hideHouseNumber}>
      <OMCheckbox
        id="hideHouseNumber"
        label={inputLabel}
        field={{ ...input, checked: input.value }}
      />
    </div>);
  };

  renderManualButtons = () => this.state.manualAddress && (
    <div className={styles.mapButtons}>
      <button onClick={this.props.isForeign ? this.saveAddressValue : this.createAddress} >
        {this.props.translate('save')}
      </button>
      <button onClick={this.handleCancelManual} >
        {this.props.translate('cancel')}
      </button>
    </div>
  );

  renderMyLocationButton = () => (
    <button
      className={styles.locationButton}
      onClick={this.handleClickMyLocation}
    >
      <i className="icon icon-location" />
    </button>
  );

  render() {
    const { error, anyTouched, reportIssue, value, locale, token, isPublishFrom } = this.props;
    const { result: address, isDefaultLanguage, name } = getLocalizedData(value || {}, locale, language.fallback);
    // const withContinueButton = get(address, 'house_number') && !get(value, 'isFull') && !this.state.manualAddress;
    const isShouldBeShow = (this.state.showConfirmationButton && get(value, 'isFull')) && !this.props.isForeign;
    return (
      <Element
        name={name}
        className={cx(
          styles.sectionBlock,
          styles.addressBlock,
          this.props.className,
          { [styles.withContinueButton]: isShouldBeShow }
        )}
      >
        {isPublishFrom && <div className={styles.title}>{this.props.translate('sections', 'address')}</div>}
        {isPublishFrom && <div
          className={styles.reportAddressIssue}
          onClick={() => reportIssue({ issueType: issueTypes.ADDRESS, reportType: reportTypes.PUBLISH })}
        >
          {this.props.translate('reportAddressIssue')}
        </div>}
        <div className={styles.locationInputWrapper}>
          <div>
            <PublishFormLocationInput
              locale={this.props.locale}
              placeholder={`${this.props.translate('addressInputPlaceHolder')}`}
              getInputData={this.handleLocationInput}
              changeValue={this.props.changeValue}
              googleService={this.googleService}
              initialValue={this.props.initialValue}
              translateTooltip={this.props.translate('enterFullAddress')}
              isInvalid={!!error && anyTouched}
              isMobile={this.props.isMobile}
              translate={this.props.translate}
              isRtl={this.props.isRtl}
              address={address || {}}
              fullAddressModel={this.props.value}
              isFullAddress={get(value, 'isFull')}
              clearAddressPart={this.clearAddressPart}
              isDefaultLanguage={isDefaultLanguage}
              withTooltipImage={get(this.props, 'value.hide_house_number')}
              isRealtor={this.props.isRealtor}
              city_name={this.state.city}
              street_name={this.state.street}
              country_name={this.state.country}
              token={token}
              manualAddress={this.state.manualAddress}
              setHomeNumberManually={this.setHomeNumberManually}
              setPreloaderState={this.setPreloaderState}
              setManualButtonState={this.setManualButtonState}
              getComponentInstance={this.getComponentInstance}
              isForeign={this.props.isForeign}
              name={this.props.name}
              rootTranslate={this.props.rootTranslate}
              isIsraelOnly={this.props.isIsraelOnly}
            />
          </div>
        </div>
        {(this.props.isRealtor && isPublishFrom) && (
          <Field
            normalize={currentValue => !!currentValue}
            name="address.hide_house_number"
            props={{ locale: this.props.locale }}
            component={this.renderHideNumber}
          />
        )}
        <div className={styles.map}>
          <div style={{ width: '100%', height: '100%' }} ref={(map) => { this.mapWrapper = map; }} />
          { this.state.showPreloader && <div className={styles.preloader}><i className="icon icon-om-spinner om-pulse" /></div>}
          {this.renderMyLocationButton()}
        </div>
        { this.state.showManualButton && !this.state.manualAddress && <div
          className={styles.mapPopup}
        >
          <div className={styles.locateManually}>{this.props.translate('locateManually')}</div>
          <button onClick={this.enableManualMode} >
            {this.props.translate('locateManuallyButton')}
          </button>
        </div>}
        {this.renderManualButtons()}
        {(isShouldBeShow && isPublishFrom) && <div onClick={this.onHideButton} className={styles.continueButton}>{this.props.translate('continueButton')}</div>}
      </Element>
    );
  }
}

PublishAddressBlock.propTypes = {
  translate: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  changeValue: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  locale: PropTypes.string,
  token: PropTypes.string,
  error: PropTypes.any,
  anyTouched: PropTypes.bool,
  initialValue: PropTypes.any,
  value: PropTypes.object,
  isRtl: PropTypes.bool,
  reportIssue: PropTypes.func,
  scrollToAddress: PropTypes.func,
  rootTranslate: PropTypes.func,
  isRealtor: PropTypes.bool,
  isForeign: PropTypes.bool,
  isPublishFrom: PropTypes.bool,
  className: PropTypes.string,
  isIsraelOnly: PropTypes.bool
};
